import React, { useEffect, useState } from 'react'
import axiosClient from 'axios-client'
import { useStateContext } from 'context/ContextProvider'
import style from 'assets/css/Table.module.css'
import Filter from 'components/Filter'
import AddButton from 'components/Buttons/Add/AddButton'
import { Account, Id, Platform, Status } from 'types'
import { Link } from 'react-router-dom'
import { Table, TableColumnsType } from 'antd'
import TableOptions from 'components/Table/TableOptions'
import CustomTag from 'components/Buttons/Tag/CustomTag'
import { updateObject } from 'utils/updateObjectList'

const columns: TableColumnsType<Account> = [
  {
    title: 'Username',
    dataIndex: 'username',
    key: 'username',
    render: (_, { id, username }) => (
      <Link
        className={style.title}
        to={'/accounts/view/' + id}
      >
        {username}
      </Link>
    ),
  },
  {
    title: 'Client',
    dataIndex: 'client',
    key: 'client',
    render: (_, { client }) =>
      client?.id ? (
        <Link
          className={style.title}
          to={'/clients/view/' + client.id}
        >
          {client.name}
        </Link>
      ) : (
        <></>
      ),
  },
  {
    title: 'Platform',
    dataIndex: 'platform',
    key: 'platform',
    render: platform => <CustomTag>{platform}</CustomTag>,
  },
  {
    title: 'Email',
    dataIndex: 'email',
    key: 'email',
  },
  {
    title: 'Country',
    dataIndex: 'country',
    key: 'country',
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    render: status => <CustomTag>{status}</CustomTag>,
  },
]

const Accounts: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([])
  const [loading, setLoading] = useState(false)

  // Get enums
  const platforms = Object.values(Platform)
  const statusVal = Object.values(Status)
  // Datalist suggestions
  const suggestions: (Platform | Status)[] = [...statusVal, ...platforms]

  const [selectedAccounts, setSelectedAccounts] = useState<Id[]>([])

  // rowSelection object indicates the need for row selection
  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[]) => {
      //console.log(`selectedRowKeys: ${selectedRowKeys}`, 'selectedRows: ', selectedRows);
      setSelectedAccounts(selectedRowKeys as Id[])
    },
  }

  useEffect(() => {
    getAccounts([])
  }, [])

  const getAccounts = (filters: string[]) => {
    setLoading(true)

    axiosClient
      .get('/accounts', {
        params: {
          filters: filters,
        },
      })
      .then(({ data }) => {
        setAccounts(data)
        setLoading(false)
      })
  }

  const updateAccountStatus = (field: string, newStatus: string) => {
    // Find the account by its ID
    const updatedAccounts = updateObject<Account>({
      objects: accounts,
      selectedIds: selectedAccounts,
      updateProperty: field,
      updateValue: newStatus,
    })

    // Update the state with the modified accounts array
    setAccounts(updatedAccounts)
  }

  return (
    <>
      <div className={style.page_title}>
        <div className={style.label}>Accounts</div>
        <div className={style.count}>{accounts?.length} Accounts</div>
        {accounts && accounts.length > 0 && (
          <div style={{ marginLeft: 'auto' }}>
            <AddButton text="Account" />
          </div>
        )}
      </div>
      <div className={style.box}>
        <Filter
          setFilters={getAccounts}
          suggestions={suggestions.sort()}
        />

        <Table
          columns={columns}
          dataSource={accounts}
          rowSelection={{
            type: 'checkbox',
            fixed: true,
            ...rowSelection,
          }}
          pagination={{
            position: ['bottomCenter'],
            pageSizeOptions: [10, 20, 50],
            showSizeChanger: true,
          }}
          locale={{
            emptyText: (
              <div className={style.no_record}>
                <div className={style.text_wrapper}>
                  No account yet! Please click on the button bellow to add new account.
                </div>
                <AddButton text="Account" />
              </div>
            ),
          }}
          loading={loading}
          rowKey={record => record.id}
          className={`${style.antTable}`}
          rowClassName={`${style.antRow}`}
          bordered
        />

        <TableOptions
          selectedRows={selectedAccounts}
          type="accounts"
          updateObject={updateAccountStatus}
        />
      </div>
    </>
  )
}

export default Accounts
